import { useLayoutEffect, useRef, type CSSProperties, type ReactNode } from 'react';
import { MathText, cn } from '@jeraisonne/ui';
import { moveMath } from '@/features/moves/move-math';
import { trousseFlight } from './kit-flight';
import { Trousse3D } from '@/features/trousse-3d';
import { trousseImage } from '@/lib/trousses';
import './kit-stage.css';

/**
 * La trousse de la démo : celle de départ, figée (le visiteur n'a pas de compte, donc pas de
 * collection — c'est la même couverture que celle du player en démo).
 */
export const DEMO_TROUSSE = {
  slug: 'graphite',
  image: trousseImage('trousses/01.webp'),
} as const;

export type MoveKind = 'savoir' | 'geste';

export interface KitToken {
  id: string;
  kind: MoveKind;
  /** le nom court du Move, celui qu'on lit sur le jeton */
  label: string;
}

const KIND_LABEL: Record<MoveKind, string> = { savoir: 'Savoir', geste: 'Geste' };

/**
 * La trousse en grand, dans son ciel, avec les jetons de Moves qui en sortent.
 *
 * ⚠️ Les jetons sont posés PAR-DESSUS la 3D (un seul canvas : rien ne peut passer derrière le
 * devant de la trousse) — ils sortent donc du haut, et la trousse ne tourne pas quand il y en a.
 */
export function KitStage({
  open = false,
  spin,
  tokens = [],
  dropId,
  size = 240,
  leaving = false,
  className,
}: {
  open?: boolean;
  spin?: boolean;
  tokens?: readonly KitToken[];
  /** l'id du jeton qui vient d'être gagné : lui seul tombe dans la trousse */
  dropId?: string;
  size?: number;
  /** elle s'en va se poser au bord droit, là où l'exercice la tient (`kit-flight.ts`) */
  leaving?: boolean;
  className?: string;
}) {
  const piece = useRef<HTMLDivElement>(null);
  // la trajectoire se mesure à l'écran : la trousse part d'où elle est, et va où l'exercice l'attend
  useLayoutEffect(() => {
    const el = piece.current;
    if (!leaving || !el) return;
    const from = el.getBoundingClientRect();
    const f = trousseFlight(from, window.innerWidth, window.innerHeight);
    el.style.setProperty('--jr-fly-x', `${f.x}px`);
    el.style.setProperty('--jr-fly-y', `${f.y}px`);
    el.style.setProperty('--jr-fly-scale', String(f.scale));
  }, [leaving]);
  return (
    <div className={cn('jr-kit-stage', className)}>
      <div className="jr-kit-sky" aria-hidden="true" />
      <div className="jr-kit-halo" aria-hidden="true" />
      <div
        ref={piece}
        className="jr-kit-piece"
        style={{ ['--jr-kit-size' as string]: `${size}px` }}
        data-fly={leaving ? 'true' : undefined}
        data-testid="kit-stage-piece"
      >
        <Trousse3D
          slug={DEMO_TROUSSE.slug}
          image={DEMO_TROUSSE.image}
          open={open}
          spin={leaving ? false : (spin ?? tokens.length === 0)}
          bumpKey={dropId}
        >
          <img className="jr-kit-flat" src={DEMO_TROUSSE.image} alt="" />
        </Trousse3D>
        {tokens.length > 0 && (
          <div className="jr-kit-tokens" data-many={tokens.length > 2 ? 'true' : undefined}>
            {tokens.map((t, i) => (
              <div
                key={t.id}
                className="jr-kit-token"
                style={fanStyle(i, tokens.length)}
                data-kind={t.kind}
                data-drop={t.id === dropId ? 'true' : undefined}
                data-testid={`kit-token-${t.kind}`}
              >
                <span className="jr-kit-token-kind">{KIND_LABEL[t.kind]}</span>
                <span className="jr-kit-token-label">{t.label}</span>
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}

/**
 * Les jetons en ÉVENTAIL : chacun penche selon son écart au centre et descend un peu sur les bords,
 * comme des cartes tenues en main. PUR.
 */
export function fanStyle(i: number, n: number): CSSProperties {
  const off = i - (n - 1) / 2;
  const step = n > 2 ? 9 : 5;
  return {
    ['--jr-fan-rot' as string]: `${off * step}deg`,
    ['--jr-fan-y' as string]: `${Math.abs(off) * (n > 2 ? 7 : 3)}px`,
  };
}

/** Les emplacements encore vides de la trousse (un par Move à gagner). */
export function KitSlots({ kinds }: { kinds: readonly MoveKind[] }) {
  return (
    <div className="jr-kit-slots" aria-hidden="true">
      {kinds.map((k, i) => (
        <div key={`${k}-${i}`} className="jr-kit-slot" data-kind={k}>
          ?
        </div>
      ))}
    </div>
  );
}

/**
 * La carte d'un Move : ce qu'il est (savoir doré / geste violet), son nom, et la ligne qu'il pose.
 * La même à son déblocage (où elle part ensuite se ranger dans la trousse) et au récap de fin.
 */
export function MoveCard({
  kind,
  name,
  writes,
  fly = false,
}: {
  kind: MoveKind;
  name: string;
  /** la ligne du savoir (un geste n'en pose pas) */
  writes?: string;
  /** elle part se ranger dans la trousse */
  fly?: boolean;
}) {
  return (
    <div
      className="jr-move-card"
      data-kind={kind}
      data-fly={fly ? 'true' : undefined}
      data-testid={`move-card-${kind}`}
    >
      <span className="jr-move-card-kind">{KIND_LABEL[kind]}</span>
      <span className="jr-move-card-name">{name}</span>
      {writes && (
        <span className="jr-move-card-writes">
          <MathText>{moveMath(writes)}</MathText>
        </span>
      )}
    </div>
  );
}

/** La frise des étapes de la démo (trousse → savoir → geste → fin). */
export function KitDots({ step, total = 5 }: { step: number; total?: number }) {
  return (
    <div className="jr-kit-dots" aria-hidden="true">
      {Array.from({ length: total }, (_, i) => (
        <span key={i} className="jr-kit-dot" data-on={i <= step ? 'true' : undefined} />
      ))}
    </div>
  );
}

/** Le cadre nuit commun à ces écrans. */
export function KitScreen({
  children,
  testId,
  overlay = false,
  scroll = false,
  leaving = false,
}: {
  children: ReactNode;
  testId?: string;
  /** par-dessus l'écran du dessous (la feuille reste montée derrière) */
  overlay?: boolean;
  /** l'écran défile s'il ne tient pas (petit téléphone) au lieu d'être coupé */
  scroll?: boolean;
  /** il s'efface en fondu pour découvrir la feuille (la trousse, elle, s'en va en volant) */
  leaving?: boolean;
}) {
  return (
    <div
      className={cn(
        'native:pt-[env(safe-area-inset-top)] bg-[#061433] px-5 pb-[max(1.5rem,env(safe-area-inset-bottom))] pt-8 text-white',
        overlay
          ? 'fixed inset-0 z-[95] overflow-y-auto'
          : cn('relative min-h-dvh', scroll ? 'overflow-y-auto' : 'overflow-hidden'),
        leaving && 'jr-kit-leaving pointer-events-none',
      )}
      data-testid={testId}
    >
      <div className="jr-kit-sky" aria-hidden="true" />
      <div className="jr-stagger relative mx-auto flex min-h-[calc(100dvh-5rem)] w-full max-w-md flex-col items-center">
        {children}
      </div>
    </div>
  );
}
